import React from 'react';

import { css, StyleSheet } from 'aphrodite';
import PropTypes from 'prop-types';

import {
  backgroundGrey,
  blackberry,
  grape,
  greyBorder,
  lightPurple2,
  mutedGray,
  shadowBlackberry,
  shadowVeryLight,
  standardGray,
  white,
} from 'lib/css/colors';

const HIERARCHY_LEVELS = ['l0', 'l1', 'l2', 'l3', 'l4'];

function HierarchyBreadcrumb({ hierarchy, targetAttribute }) {
  const levels = hierarchy
    ? HIERARCHY_LEVELS.map(level => ({
        level: level.toUpperCase(),
        data: hierarchy[level],
      })).filter(item => item.data)
    : [];

  return (
    <div className={css(styles.hierarchyBar)}>
      <div className={css(styles.hierarchyChips)}>
        {levels.map((item, index) => (
          <React.Fragment key={item.level}>
            <div
              className={css(
                styles.hierarchyChip,
                item.level === 'L4' && styles.hierarchyChipActive,
              )}
            >
              <span
                className={css(styles.levelBadge, item.level === 'L4' && styles.levelBadgeActive)}
              >
                {item.level}
              </span>
              <span
                className={css(styles.levelName, item.level === 'L4' && styles.levelNameActive)}
              >
                {item.data.name}
              </span>
            </div>
            {index < levels.length - 1 && <span className={css(styles.hierarchySeparator)}>›</span>}
          </React.Fragment>
        ))}
      </div>

      {targetAttribute && (
        <div className={css(styles.targetAttributePill)}>
          <span className={css(styles.targetAttributeLabel)}>Target Attribute</span>
          <span className={css(styles.targetAttributeValue)}>{targetAttribute}</span>
        </div>
      )}
    </div>
  );
}

HierarchyBreadcrumb.propTypes = {
  hierarchy: PropTypes.object,
  targetAttribute: PropTypes.string,
};

const styles = StyleSheet.create({
  hierarchyBar: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    marginBottom: '8px',
    borderBottom: `1px solid ${greyBorder}`,
    flexShrink: 0,
  },
  hierarchyChips: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    minWidth: 0,
  },
  hierarchyChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px 4px 4px',
    backgroundColor: backgroundGrey,
    borderRadius: '20px',
    boxShadow: `0 1px 3px ${shadowVeryLight}`,
  },
  hierarchyChipActive: {
    backgroundColor: blackberry,
    boxShadow: `0 2px 6px ${shadowBlackberry}`,
  },
  levelBadge: {
    fontSize: '10px',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: '10px',
    backgroundColor: greyBorder,
    color: standardGray,
    minWidth: '22px',
    textAlign: 'center',
  },
  levelBadgeActive: {
    backgroundColor: grape,
    color: white,
  },
  levelName: {
    fontSize: '12px',
    fontWeight: 500,
    color: mutedGray,
    maxWidth: '160px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  levelNameActive: {
    color: lightPurple2,
    fontWeight: 600,
  },
  hierarchySeparator: {
    fontSize: '16px',
    color: greyBorder,
    lineHeight: 1,
    flexShrink: 0,
  },
  targetAttributePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: blackberry,
    borderRadius: '20px',
    padding: '5px 14px',
    flexShrink: 0,
  },
  targetAttributeLabel: {
    fontSize: '11px',
    fontWeight: 700,
    color: lightPurple2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  targetAttributeValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: white,
  },
});

export default HierarchyBreadcrumb;
